import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DISCOVER_WISEREADS_READER_DOC_URL } from 'shared/constants.platform';
import { showModal } from 'shared/foreground/actions/modals';
import { saveLinkToReader } from 'shared/foreground/stateUpdaters/persistentStateUpdaters/documents/anyDocument';
import { useHasDocumentContentInitialized } from 'shared/foreground/useHasDocumentContentInitialized';
import { forceChunkContentLoadForContainer, getScrollTargetForInternalLink, isExternalLink } from 'shared/foreground/utils/chunkedScrolling';
import copyTextToClipboard from 'shared/foreground/utils/copyTextToClipboard';
import getClosestHTMLElement from 'shared/foreground/utils/getClosestHTMLElement';
import getUIFriendlyNameForDocumentLocation from 'shared/foreground/utils/getUIFriendlyNameForDocumentLocation';
import useDocumentLocations from 'shared/foreground/utils/useDocumentLocations';
import { DocumentLocation } from 'shared/types';
import { ShortcutId } from 'shared/types/keyboardShortcuts';
import exceptionHandler from 'shared/utils/exceptionHandler.platform';
import urlJoin from 'shared/utils/urlJoin';

import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { openURL } from '../../utils/openURL';
import { scrollToDocElement } from '../../utils/scrollToDocElement';
import { useShortcutsMap } from '../../utils/shortcuts';
import { getDisplayShortcutKeys } from '../../utils/shortcuts/shortcuts';
import Button from '../Button';
import StrokeAddCircleIcon from '../icons/StrokeAddCircleIcon';
import StrokeArrowUpRightIcon from '../icons/StrokeArrowUpRightIcon';
import StrokeLinkIcon from '../icons/StrokeLinkIcon';
import { discoverWisereadsModalId } from '../Modals/AddWisereadsItemsModal';
import Popover from './Popover';
import styles from './SaveLinkInAppPopover.module.css';

export default function SaveLinkInAppPopover({
  contentRef,
  focusTargetRef,
  isChunked,
}: {
  contentRef: React.MutableRefObject<HTMLDivElement>;
  focusTargetRef: React.MutableRefObject<HTMLElement | undefined>;
  isChunked: boolean;
}) {
  const history = useHistory();
  const [clickedLink, setClickedLink] = useState<HTMLAnchorElement | undefined>(undefined);
  const hideActionsPopoverShown = useCallback(() => setClickedLink(undefined), []);
  const shortcutsMap = useShortcutsMap();
  const hasInitializedContent = useHasDocumentContentInitialized();

  // We store the index of the rect because some links will break line and we
  // want to always show the popover under the line the user clicked.
  //
  // E.g:
  // Fofofofofofofo here it is
  // a long link fofofofofofofo
  //
  // We use link.getClientRects() to detect if the user clicked "here it is" or
  // "a long link".
  const [rectIndex, setRectIndex] = useState(0);

  // Adding this flag to prevent the `enter` shortcut firing after
  // opening the doc with `enter`.
  const [shouldRunShortcut, setShouldRunShortcut] = useState(false);

  useEffect(() => {
    setTimeout(() => setShouldRunShortcut(true), 1000);
  }, []);

  useEffect(() => {
    if (!contentRef.current || !hasInitializedContent) {
      return;
    }

    let isMounted = true;

    const onContentClick = async (event: MouseEvent) => {
      if (!isMounted) {
        return;
      }

      const target = event.target as HTMLElement;

      // Close popover when click outside content
      if (!contentRef.current?.contains(target)) {
        setClickedLink(undefined);
        return;
      }

      // Override popover when doing cmd/ctrl + click
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      const closestLink = target.closest('a');

      // Handle non-link clicks within content
      if (!closestLink?.href) {
        setClickedLink(undefined);
        return;
      }

      // Handle special WiseReads link
      if (closestLink.href === DISCOVER_WISEREADS_READER_DOC_URL) {
        event.preventDefault();
        showModal({ id: discoverWisereadsModalId }, { userInteraction: 'click' });
        return;
      }

      // Handle internal links (anchor links within the document)
      if (isInternalLink(closestLink)) {
        event.preventDefault();

        const hrefAttributeValue = closestLink.getAttribute('href');
        if (hrefAttributeValue) {
          const scrollTarget = await getScrollTargetForInternalLink(hrefAttributeValue, contentRef.current, forceChunkContentLoadForContainer, closestLink);

          if (!isMounted) {
            return;
          }

          if (!scrollTarget) {
            exceptionHandler.captureException('Could not find scroll target', { extra: { hrefAttributeValue } });
            return;
          }

          await scrollToDocElement(scrollTarget, isChunked);
        }
        return;
      }

      // Handle external links
      if (contentRef.current.contains(closestLink)) {
        event.preventDefault();

        // If we clicked the same link, close the popover
        if (clickedLink?.isEqualNode(closestLink)) {
          setClickedLink(undefined);
        } else {
          // Otherwise, open the popover.
          // We set it to undefined before to force a re-render.
          setClickedLink(undefined);
          setClickedLink(closestLink);

          const rectIndex = Array.from(closestLink.getClientRects()).findIndex((rect) => {
            return (
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom
            );
          });

          setRectIndex(rectIndex > -1 ? rectIndex : 0);
        }
      }
    };

    document.addEventListener('click', onContentClick);

    return () => {
      isMounted = false;
      document.removeEventListener('click', onContentClick);
    };
  }, [contentRef, hasInitializedContent, clickedLink, isChunked]);

  const goToNewDoc = useCallback(
    (docId: string) => history.push(urlJoin(['/', DocumentLocation.New, 'read', docId])),
    [history],
  );

  const documentLocations = useDocumentLocations();

  const onSaveLinkToReader = useCallback(
    async (url: string, tooltipContent?: string) => {
      setClickedLink(undefined);
      await saveLinkToReader({
        newDocumentLocation: documentLocations[0],
        onButtonClick: goToNewDoc,
        tooltipContent,
        url,
      });
    },
    [documentLocations, goToNewDoc],
  );

  const getFirstClickableLink = useCallback(
    (htmlEl: HTMLElement) => {
      const links = htmlEl.querySelectorAll('a');
      return Array.from(links).find((link) => {
        return link.href && !isInternalLink(link);
      });
    },
    [],
  );

  useKeyboardShortcut(
    shortcutsMap[ShortcutId.Enter],
    useCallback(() => {
      if (!focusTargetRef.current) {
        return;
      }

      if (clickedLink) {
        openURL(clickedLink.href, '_blank');
        return;
      }

      const firstLink = getFirstClickableLink(focusTargetRef.current);

      if (firstLink) {
        openURL(firstLink.href, '_blank');
      }
    }, [focusTargetRef, clickedLink, getFirstClickableLink]),
    {
      description: 'Open first link',
      preferredEventTrigger: 'keydown',
    },
  );

  useKeyboardShortcut(
    shortcutsMap[ShortcutId.SaveLinkToReader],
    useCallback(() => {
      if (!focusTargetRef.current || !shouldRunShortcut) {
        return;
      }

      if (clickedLink) {
        onSaveLinkToReader(clickedLink.href);
        return;
      }

      const firstLink = getFirstClickableLink(focusTargetRef.current);

      if (firstLink) {
        onSaveLinkToReader(
          firstLink.href,
          `First link saved to ${getUIFriendlyNameForDocumentLocation(documentLocations[0])}`,
        );
      }
    }, [
      clickedLink,
      documentLocations,
      focusTargetRef,
      getFirstClickableLink,
      onSaveLinkToReader,
      shouldRunShortcut,
    ]),
    {
      description: 'Save first link to reader',
      preferredEventTrigger: 'keyup',
    },
  );

  const getBoundingClientRect = useMemo(() => {
    if (clickedLink) {
      return () => clickedLink.getClientRects()[rectIndex];
    }

    return undefined;
  }, [clickedLink, rectIndex]);

  if (!clickedLink) {
    return null;
  }

  return (
    <Popover
      className={styles.actionsPopover}
      getBoundingClientRect={getBoundingClientRect}
      hidePopover={hideActionsPopoverShown}
      isShown
      pointerDownTimeout={250}
      popperOptions={{
        placement: 'bottom-start',
      }}
      preventOverflow
      reference={clickedLink}
      shouldHideOnBlur={false}
      shouldHideOnClickOutside={false}
    >
      <ul className={styles.actionsPopoverList}>
        <li className={styles.actionsPopoverListItem}>
          <a
            className={styles.actionsPopoverAction}
            href={clickedLink.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={hideActionsPopoverShown}
            data-open-on-desktop
          >
            <StrokeArrowUpRightIcon />
            Open link in new tab
            <span className={styles.actionsPopoverShortcut}>
              {getDisplayShortcutKeys(shortcutsMap[ShortcutId.Enter])}
            </span>
          </a>
        </li>

        <li className={styles.actionsPopoverListItem}>
          <Button
            className={styles.actionsPopoverAction}
            onClick={() => onSaveLinkToReader(clickedLink.href)}
            variant="unstyled"
          >
            <StrokeAddCircleIcon />
            Save link to Reader
            <span className={styles.actionsPopoverShortcut}>
              {getDisplayShortcutKeys(shortcutsMap[ShortcutId.SaveLinkToReader])}
            </span>
          </Button>
        </li>

        <li className={styles.actionsPopoverListItem}>
          <Button
            className={styles.actionsPopoverAction}
            onClick={() => {
              copyTextToClipboard(clickedLink.href);
              hideActionsPopoverShown();
            }}
            variant="unstyled"
          >
            <StrokeLinkIcon />
            Copy link to clipboard
          </Button>
        </li>
      </ul>
    </Popover>
  );
}

/**
 * Checks if a link is internal to the current document.
 *
 * This is used on web to determine if the link is internal to the document i.e. its href references something within the document.
 */
function isInternalLink(clickedLinkElement: HTMLAnchorElement): boolean {
  const clickedHref = clickedLinkElement.href;
  // In some cases element.href returns absolute href instead of the real element href attribute value
  const hrefAttributeValue = clickedLinkElement.getAttribute('href');

  if (clickedHref.startsWith('#') || hrefAttributeValue?.startsWith('#')) {
    return true;
  }

  const isWithinContentContainer = Boolean(getClosestHTMLElement(clickedLinkElement, (ancestor: HTMLElement) =>
    ancestor.classList?.contains('rw-outer-content')));

  if (isWithinContentContainer) {
    return true;
  }

  const isExternal = hrefAttributeValue && isExternalLink(hrefAttributeValue);

  if (isExternal) {
    const currentHref = window.location.href;
    if (clickedHref.includes(currentHref)) {
      return true;
    }
    return false;
  }

  return true;
}
